import React, { forwardRef, useCallback } from 'react';
import { css, cx } from '@emotion/css';
import { theme, IconRadioSelected, IconRadioUnselected, } from '@contrast-security-inc/design-system-foundations';
import { IconInputBase, iconInputStyles } from '../IconInputBase/IconInputBase';
const radioStyles = css `
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;
const radioInputStyles = css `
  cursor: pointer;

  & + label svg {
    height: 20px;
    width: 20px;
  }

  & + label span {
    height: 20px;
  }

  &:disabled + label span,
  &:disabled + label {
    cursor: not-allowed;
  }

  :focus-visible + label svg {
    outline: 2px solid ${theme.color.turquoise90};
    outline-offset: -2px;
    border-radius: 50%;
  }
`;
const disabledStyles = css `
  svg { /* stylelint-disable-line no-descending-specificity */
    opacity: 0.3;
  }
`;
const selectedHoverStyles = css `
  svg:hover {  /* stylelint-disable-line no-descending-specificity */
    fill: ${theme.color.turquoise90};
  }
`;
const selectedStyles = css `
  &:focus-visible + label svg {
    outline: 2px solid ${theme.color.neutral90};
    box-sizing: border-box;
  }
`;
const unselectedHoverStyles = css `
  svg:hover {  /* stylelint-disable-line no-descending-specificity */
    fill: ${theme.color.neutral90};
  }
`;
const unselectedStyles = css `
  :focus-within {
    svg {  /* stylelint-disable-line no-descending-specificity */
      outline: 2px auto ${theme.color.turquoise70};
      box-sizing: border-box;
    }
  }
`;
const getSelectedStyles = (disabled) => {
    return cx(!disabled && selectedHoverStyles);
};
const getUnselectedStyles = (disabled) => {
    return cx(unselectedStyles, !disabled && unselectedHoverStyles);
};
export const Radio = forwardRef(({ 'aria-label': ariaLabel, className, error, errorPlacement, id, isChecked = false, isDisabled = false, label, onChange, style, testId, useInlineError, value, ...rest }, ref) => {
    const iconProps = {
        size: 'md',
        testId: testId && `${testId}-icon`,
    };
    const getIcon = (checked) => {
        if (checked) {
            return React.createElement(IconRadioSelected, { color: "turquoise70", ...iconProps });
        }
        else {
            return React.createElement(IconRadioUnselected, { color: "neutral70", ...iconProps });
        }
    };
    const icon = getIcon(isChecked);
    const classes = cx(isChecked ? getSelectedStyles(isDisabled) : getUnselectedStyles(isDisabled), radioStyles, isDisabled ? disabledStyles : null, className, 'ds-radio');
    const changeHandler = useCallback((event) => (!isDisabled ? onChange(event, event?.target?.checked) : undefined), [onChange, isDisabled]);
    return (React.createElement(React.Fragment, null, React.cloneElement(React.createElement(IconInputBase, { "aria-label": ariaLabel, className: classes, error: error, errorPlacement: errorPlacement, id: id, isDisabled: isDisabled, label: label, renderInput: () => (React.createElement("input", { checked: isChecked, className: cx(radioInputStyles, iconInputStyles(isDisabled), isChecked && selectedStyles), disabled: isDisabled, onChange: changeHandler, type: "radio", value: value, ...rest })), style: style, testId: testId, useInlineError: useInlineError },
        React.createElement("span", { className: radioStyles, "data-testid": testId && `${testId}-radio`, style: style }, icon)), { ref })));
});
Radio.displayName = 'Radio';
